import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { API } from "../../../App";
import { FileText } from "lucide-react";

// Documentos enviados pelos pilotos que esperam a aprovação do DI. Aprovar grava a validade na ficha do piloto.
const dataPt = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const erro = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Não foi possível concluir.");

export default function PendingPilotDocuments({ onChanged }) {
  const [pedidos, setPedidos] = useState([]);
  const [recusando, setRecusando] = useState(null);
  const [nota, setNota] = useState("");
  const [aGravar, setAGravar] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/di/documents/aprovacao/pedidos`, { withCredentials: true });
      setPedidos(data || []);
    } catch {
      setPedidos([]);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const aprovar = async (id) => {
    setAGravar(true);
    try {
      await axios.post(`${API}/di/documents/aprovacao/${id}/aprovar`, {}, { withCredentials: true });
      toast.success("Documento aprovado. Validade atualizada na ficha.");
      carregar();
      onChanged?.();
    } catch (e) {
      toast.error(erro(e));
    } finally {
      setAGravar(false);
    }
  };

  const recusar = async (id) => {
    if (nota.trim().length < 3) {
      toast.error("Escreve o motivo da recusa (mínimo 3 caracteres).");
      return;
    }
    setAGravar(true);
    try {
      await axios.post(`${API}/di/documents/aprovacao/${id}/recusar`, { nota: nota.trim() }, { withCredentials: true });
      toast.success("Documento recusado. O piloto vê o motivo.");
      setRecusando(null);
      setNota("");
      carregar();
      onChanged?.();
    } catch (e) {
      toast.error(erro(e));
    } finally {
      setAGravar(false);
    }
  };

  if (pedidos.length === 0) return null;

  return (
    <Card className="instrument-panel" data-testid="di-docs-pendentes">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-amber-300">
          <FileText className="w-5 h-5" /> Documentos para aprovar ({pedidos.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        {pedidos.map((p) => (
          <div key={p.id} className="border-t border-slate-800 pt-2 space-y-2" data-testid="di-doc-pendente">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-slate-200">
                <span className="font-medium">{p.pilot_name || "Piloto"}</span>{p.owner_kind === "instructor" && <span className="text-slate-400"> (instrutor)</span>} · {p.label} · validade proposta {dataPt(p.valid_until)}
              </span>
              <span className="flex items-center gap-2">
                <a href={`${API}/di/documents/${p.id}/download`} target="_blank" rel="noreferrer" className="text-sky-300 underline">Ver ficheiro</a>
                <Button size="sm" onClick={() => aprovar(p.id)} disabled={aGravar} data-testid="di-doc-aprovar">Aprovar</Button>
                <Button size="sm" variant="outline" onClick={() => { setRecusando(recusando === p.id ? null : p.id); setNota(""); }} data-testid="di-doc-recusar">Recusar</Button>
              </span>
            </div>
            {recusando === p.id && (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Motivo da recusa" className="bg-slate-800 border-slate-700 flex-1 min-w-0" data-testid="di-doc-nota" />
                <Button size="sm" variant="destructive" onClick={() => recusar(p.id)} disabled={aGravar}>Confirmar recusa</Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
